import { type ReactNode, useState } from 'react';
import { Badge, Button, Flag, type ServerState, SignalMeter } from './basics.tsx';
import { Icon, type IconName } from './Icon.tsx';
import { useUiText } from './text.tsx';

export function BalanceCard({
  amount,
  approx,
  monthly,
  runway,
  runwayTone = 'ok',
  controls,
  note,
  onTopUp,
  onHistory,
}: {
  amount: string;
  approx?: string;
  monthly?: string;
  runway?: string;
  runwayTone?: 'ok' | 'warn' | 'crit';
  controls?: ReactNode;
  note?: ReactNode;
  onTopUp?: () => void;
  onHistory?: () => void;
}) {
  const text = useUiText();
  return (
    <section className="ul-bal" aria-label={text.balance}>
      <div className="ul-bal__head">
        <span className="ul-bal__label">{text.balance}</span>
        {onHistory ? (
          <button type="button" className="ul-link" onClick={onHistory}>
            {text.history}
            <Icon name="chevron" size={16} />
          </button>
        ) : null}
      </div>
      {controls ? <div className="ul-bal__controls">{controls}</div> : null}
      <div className="ul-bal__amount">{amount}</div>
      {approx ? <div className="ul-bal__approx">{approx}</div> : null}
      {monthly || runway ? (
        <dl className="ul-bal__stats">
          {monthly ? (
            <div>
              <dt>{text.monthlySpend}</dt>
              <dd className="ul-num">{monthly}</dd>
            </div>
          ) : null}
          {runway ? (
            <div>
              <dt>{text.runway}</dt>
              <dd className={`ul-bal__runway ul-bal__runway--${runwayTone}`}>{runway}</dd>
            </div>
          ) : null}
        </dl>
      ) : (
        <div style={{ height: 16 }} />
      )}
      <Button variant="primary" size="l" block icon="plus" onClick={onTopUp}>
        {text.topUp}
      </Button>
      {note ? <div className="ul-bal__note">{note}</div> : null}
    </section>
  );
}

export function ActionTile({
  icon,
  title,
  subtitle,
  onClick,
}: {
  icon: IconName;
  title: string;
  subtitle?: string;
  onClick?: () => void;
}) {
  return (
    <button type="button" className="ul-tile" onClick={onClick}>
      <span className="ul-tile__top">
        <span className="ul-row__icon">
          <Icon name={icon} size={18} />
        </span>
        <Icon name="chevron" size={18} className="ul-row__chev" />
      </span>
      <span className="ul-tile__title">{title}</span>
      {subtitle ? <span className="ul-tile__sub">{subtitle}</span> : null}
    </button>
  );
}

export function ServerCard({
  name,
  state,
  region,
  country,
  specs = [],
  ip,
  onCopy,
}: {
  name: string;
  state: ServerState;
  region: string;
  /** ISO code for the flag next to the region */
  country?: string;
  specs?: string[];
  ip?: string;
  onCopy?: (ip: string) => void;
}) {
  const text = useUiText();
  const [done, setDone] = useState(false);
  const copy = () => {
    if (!ip) return;
    navigator.clipboard?.writeText(ip).catch(() => {});
    onCopy?.(ip);
    setDone(true);
    setTimeout(() => setDone(false), 1500);
  };
  return (
    <article className="ul-srv">
      <div className="ul-srv__top">
        <span className="ul-srv__name">{name}</span>
        <SignalMeter state={state} showLabel />
      </div>
      <div className="ul-srv__spec">
        {country ? <Flag country={country} size={16} /> : null}
        <span>{[region, ...specs].join(' · ')}</span>
      </div>
      {ip ? (
        <div className="ul-srv__ip">
          <span className="ul-mono">{ip}</span>
          <button
            type="button"
            className={`ul-copy${done ? ' is-done' : ''}`}
            onClick={copy}
            aria-live="polite"
          >
            <Icon name={done ? 'check' : 'copy'} size={16} />
            {done ? text.copied : text.copy}
          </button>
        </div>
      ) : null}
    </article>
  );
}

export interface TabItem<T extends string> {
  id: T;
  label: string;
  icon: IconName;
}

export function TabBar<T extends string>({
  items,
  active,
  onChange,
}: {
  items: TabItem<T>[];
  active: T;
  onChange?: (id: T) => void;
}) {
  const text = useUiText();
  return (
    <nav className="ul-tabs" aria-label={text.sections}>
      {items.map((it) => {
        const current = it.id === active;
        return (
          <button
            key={it.id}
            type="button"
            className="ul-tab"
            aria-current={current ? 'page' : undefined}
            onClick={() => onChange?.(it.id)}
          >
            <span className="ul-tab__icon">
              <Icon name={it.icon} size={22} weight={current ? 'fill' : 'regular'} />
            </span>
            <span className="ul-tab__label">{it.label}</span>
          </button>
        );
      })}
    </nav>
  );
}

const DOMAIN_STATUS = {
  available: ['ok', 'domainAvailable'],
  taken: ['neutral', 'domainTaken'],
  premium: ['warn', 'domainPremium'],
} as const;

export function DomainResultRow({
  label,
  tld,
  status,
  price,
  renew,
  selected,
  onSelect,
}: {
  label: string;
  tld: string;
  status: 'available' | 'taken' | 'premium';
  price?: string | null;
  renew?: string | null;
  selected?: boolean;
  onSelect?: () => void;
}) {
  const t = useUiText();
  const [tone, key] = DOMAIN_STATUS[status];
  const selectable = status === 'available' && !!onSelect;
  const body = (
    <>
      <span className="ul-row__body">
        <span className="ul-dom__name">
          {label}
          <b>.{tld}</b>
        </span>
        <span>
          <Badge tone={tone}>{t[key]}</Badge>
        </span>
      </span>
      {status === 'taken' ? null : (
        <span className="ul-dom__price">
          <span className="ul-dom__amount">{price ?? t.priceOnRequest}</span>
          {renew ? <span className="ul-dom__renew">{t.renewPerYear(renew)}</span> : null}
        </span>
      )}
      {selectable ? <Icon name="chevron" size={18} className="ul-row__chev" /> : null}
    </>
  );
  const className = `ul-row ul-dom${status === 'taken' ? ' ul-dom--taken' : ''}${selected ? ' ul-dom--selected' : ''}`;
  return selectable ? (
    <button type="button" className={className} onClick={onSelect}>
      {body}
    </button>
  ) : (
    <div className={className}>{body}</div>
  );
}
